import {
  AddPolicyGroupLocationsRequest,
  ApplyFindingsRequest,
  ApplyFindingsResult,
  BulkApplyRequest,
  BulkApplyResult,
  BulkAssignRequest,
  BulkChanges,
  ColumnMappingPreview,
  ColumnTarget,
  CreateDocumentUploadRequest,
  DeleteUploadResult,
  DismissUnmatchedRowRequest,
  DocumentIngestLocationOption,
  DocumentUploadInterface,
  DocumentUploadUrlResult,
  DownloadUrlResult,
  FinishReviewResult,
  PolicyFieldCandidate,
  ResolveDuplicateConflictRequest,
  ScanPendingRequest,
  ScanPendingResult,
  UpdateBulkFindingExclusionRequest,
  UpdateBulkFindingRequest,
  UpdateColumnMappingRequest,
  UpdatePolicyFindingRequest,
  UpdatePolicyReviewRequest
} from "@swivl-mono/types";

import { APIService } from "../Service/API.Service";

// LINK apps/server/src/API/DocumentIngest/DocumentIngest.Controller.ts
export class DocumentIngestAPI {
  /**
   * Presigned upload URL (neutral key — doc type is auto-detected server-side).
   * Returns `supported: false` with no URL for unsupported formats. On success,
   * PUT the file to `uploadUrl`, then call `createUpload` with `s3Key`.
   */
  static async getUploadUrl(orgId: number, fileName: string): Promise<DocumentUploadUrlResult> {
    return APIService.Server.get<DocumentUploadUrlResult>(`/document-ingest/org/${orgId}/upload-url`, { fileName });
  }

  /** Create the upload record (no scan yet — call `scanPending` after picking locations). */
  static async createUpload(orgId: number, req: CreateDocumentUploadRequest): Promise<DocumentUploadInterface> {
    return APIService.Server.post<DocumentUploadInterface>(`/document-ingest/org/${orgId}/uploads`, req);
  }

  /** Start the scan for all not-yet-scanned uploads, scoped to the chosen locations. */
  static async scanPending(orgId: number, req: ScanPendingRequest): Promise<ScanPendingResult> {
    return APIService.Server.post<ScanPendingResult>(`/document-ingest/org/${orgId}/scan`, req);
  }

  static async listUploads(orgId: number): Promise<DocumentUploadInterface[]> {
    return APIService.Server.get<DocumentUploadInterface[]>(`/document-ingest/org/${orgId}/uploads`);
  }

  /** Remove an upload (its S3 object + record). Used to clear a staged file before scanning. */
  static async deleteUpload(orgId: number, id: number): Promise<DeleteUploadResult> {
    return APIService.Server.delete<DeleteUploadResult>(`/document-ingest/org/${orgId}/uploads/${id}`);
  }

  /** Re-run a scan that failed on our side, keeping its chosen scope, so no re-upload is needed. */
  static async retryScan(orgId: number, id: number): Promise<DocumentUploadInterface> {
    return APIService.Server.post<DocumentUploadInterface>(`/document-ingest/org/${orgId}/uploads/${id}/retry-scan`, {});
  }

  /** Operator sign-off on this document's review — stamps confirmedAt only; never force-applies whatever is still pending. */
  static async finishReview(orgId: number, id: number): Promise<FinishReviewResult> {
    return APIService.Server.post<FinishReviewResult>(`/document-ingest/org/${orgId}/uploads/${id}/finish`, {});
  }

  static async getUpload(orgId: number, id: number): Promise<DocumentUploadInterface | null> {
    return APIService.Server.get<DocumentUploadInterface | null>(`/document-ingest/org/${orgId}/uploads/${id}`);
  }

  /** Short-lived signed URL to view/download the original uploaded file. */
  static async getDownloadUrl(orgId: number, id: number): Promise<DownloadUrlResult | null> {
    return APIService.Server.get<DownloadUrlResult | null>(`/document-ingest/org/${orgId}/uploads/${id}/download-url`);
  }

  /** The pre-scan column-mapping preview (computed eagerly on upload); null for a non-tabular file or while still computing. */
  static async getColumnMapping(orgId: number, id: number): Promise<ColumnMappingPreview | null> {
    return APIService.Server.get<ColumnMappingPreview | null>(`/document-ingest/org/${orgId}/uploads/${id}/column-mapping`);
  }

  /** Apply field-target overrides / cell corrections / an orientation choice, and/or confirm the column mapping before scanning. */
  static async updateColumnMapping(orgId: number, id: number, req: UpdateColumnMappingRequest): Promise<ColumnMappingPreview | null> {
    return APIService.Server.post<ColumnMappingPreview | null>(`/document-ingest/org/${orgId}/uploads/${id}/column-mapping`, req);
  }

  /** Per-location review for a multi-location spreadsheet: data changes (table), policy counts, unmatched rows. */
  static async getBulkChanges(orgId: number, id: number): Promise<BulkChanges | null> {
    return APIService.Server.get<BulkChanges | null>(`/document-ingest/org/${orgId}/uploads/${id}/bulk-changes`);
  }

  /** Apply the interpreted changes to the given locations (or all matched). */
  static async applyBulk(orgId: number, id: number, req: BulkApplyRequest): Promise<BulkApplyResult> {
    return APIService.Server.post<BulkApplyResult>(`/document-ingest/org/${orgId}/uploads/${id}/bulk-apply`, req);
  }

  /** Manually assign an unmatched spreadsheet row to a location — or every location in a group; returns the refreshed review. */
  static async assignBulkRow(orgId: number, id: number, req: BulkAssignRequest): Promise<BulkChanges | null> {
    return APIService.Server.post<BulkChanges | null>(`/document-ingest/org/${orgId}/uploads/${id}/bulk-assign`, req);
  }

  /** Dismiss (or restore) an unmatched row the operator says isn't one of our locations; returns the refreshed review. */
  static async dismissUnmatchedRow(orgId: number, id: number, req: DismissUnmatchedRowRequest): Promise<BulkChanges | null> {
    return APIService.Server.post<BulkChanges | null>(`/document-ingest/org/${orgId}/uploads/${id}/bulk-dismiss`, req);
  }

  /** Stage an inline edit to a matched location's detail finding (Cortex/schedule); returns the refreshed review. */
  static async updateBulkFinding(orgId: number, id: number, req: UpdateBulkFindingRequest): Promise<BulkChanges | null> {
    return APIService.Server.post<BulkChanges | null>(`/document-ingest/org/${orgId}/uploads/${id}/bulk-finding`, req);
  }

  /** Dismiss (or restore) a location's detail finding(s) — one field, or every one (mapKey omitted). Returns the refreshed review. */
  static async updateBulkFindingExclusion(orgId: number, id: number, req: UpdateBulkFindingExclusionRequest): Promise<BulkChanges | null> {
    return APIService.Server.post<BulkChanges | null>(`/document-ingest/org/${orgId}/uploads/${id}/bulk-finding/exclusion`, req);
  }

  /** Apply findings as the operator approves them (per-policy) to the source's scope. */
  static async applyFindings(orgId: number, id: number, req: ApplyFindingsRequest): Promise<ApplyFindingsResult> {
    return APIService.Server.post<ApplyFindingsResult>(`/document-ingest/org/${orgId}/uploads/${id}/apply-findings`, req);
  }

  /** A document's structured Policy-Builder candidates for one scope, diffed against current answers (review-only). */
  static async getPolicyCandidates(orgId: number, id: number, groupId?: number, locationId?: number): Promise<PolicyFieldCandidate[]> {
    return APIService.Server.get<PolicyFieldCandidate[]>(`/document-ingest/org/${orgId}/uploads/${id}/policy-candidates`, { groupId, locationId });
  }

  /** Edit one structured policy-field finding's proposed value before apply (persists to the document). */
  static async updatePolicyFinding(orgId: number, id: number, req: UpdatePolicyFindingRequest): Promise<DocumentUploadInterface | null> {
    return APIService.Server.post<DocumentUploadInterface | null>(`/document-ingest/org/${orgId}/uploads/${id}/policy-finding`, req);
  }

  /** Toggle a grouped policy update's "Skip this one"/"Include" state; returns the refreshed review. */
  static async updatePolicyReview(orgId: number, id: number, req: UpdatePolicyReviewRequest): Promise<BulkChanges | null> {
    return APIService.Server.post<BulkChanges | null>(`/document-ingest/org/${orgId}/uploads/${id}/policy-review`, req);
  }

  /** Resolve a duplicate-location conflict (two rows matched one location, disagreeing on a field). */
  static async resolveDuplicateConflict(orgId: number, id: number, req: ResolveDuplicateConflictRequest): Promise<BulkChanges | null> {
    return APIService.Server.post<BulkChanges | null>(`/document-ingest/org/${orgId}/uploads/${id}/duplicate-conflict`, req);
  }

  /** Every org location with its address context, for the review's location pickers. */
  static async listLocationOptions(orgId: number): Promise<DocumentIngestLocationOption[]> {
    return APIService.Server.get<DocumentIngestLocationOption[]>(`/document-ingest/org/${orgId}/locations`);
  }

  /** Every target a column can be mapped to — powers the pre-scan column-mapping modal's `<select>`. */
  static async getColumnTargetCatalog(orgId: number): Promise<ColumnTarget[]> {
    return APIService.Server.get<ColumnTarget[]>(`/document-ingest/org/${orgId}/column-target-catalog`);
  }

  /** Add a location the scanned document never covered to a policy update; returns the refreshed review. */
  static async addPolicyGroupLocations(orgId: number, id: number, req: AddPolicyGroupLocationsRequest): Promise<BulkChanges | null> {
    return APIService.Server.post<BulkChanges | null>(`/document-ingest/org/${orgId}/uploads/${id}/policy-review/add-locations`, req);
  }
}
